import React, { useEffect, useRef, useState } from 'react'
import videojs from 'video.js'
import 'video.js/dist/video-js.css'
import 'videojs-mux'
import 'videojs-overlay'
import 'videojs-contrib-quality-levels'
import 'videojs-max-quality-selector'
import 'videojs-overlay/dist/videojs-overlay.css'
import styles from './VideoJS.module.scss'
import { LoadingIndicator } from '../LoadingIndicator/LoadingIndicator'

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import airplay from '@silvermine/videojs-airplay'
import '@silvermine/videojs-airplay/dist/silvermine-videojs-airplay.css'

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import chromecast from '@silvermine/videojs-chromecast'
import '@silvermine/videojs-chromecast/dist/silvermine-videojs-chromecast.css'

// Import the Chromecast context
import { useChromecast, ChromecastProvider } from '../../contexts/ChromecastContext'

// Initialize plugins globally
airplay(videojs)

// Initialize Chromecast plugin with a delay to ensure the Cast API is ready
let castApiInitialized = false

// Function to initialize plugins in sequence
const initializePlugins = async () => {
  try {
    // Step 1: Initialize Airplay
    console.log('Initializing Airplay plugin...')
    airplay(videojs)

    // Step 2: Initialize Chromecast
    console.log('Initializing Chromecast plugin...')
    await new Promise<void>((resolve) => {
      setTimeout(() => {
        try {
          chromecast(videojs, {
            preloadWebComponents: true,
            isCastApiAvailable: () => castApiInitialized,
          })
          castApiInitialized = true
          console.log('Chromecast plugin initialized')
          resolve()
        } catch (error) {
          console.error('Error initializing Chromecast plugin:', error)
          resolve() // Resolve even on error to continue the flow
        }
      }, 1000)
    })

    console.log('All plugins initialized successfully')
    return true
  } catch (error) {
    console.error('Error during plugin initialization:', error)
    return false
  }
}

interface Sources {
  src: string
  type: string
}

interface Props {
  options: {
    sources: Sources[]
  }
  onReady: (player: videojs.Player) => void
  title: string
}

export const VideoJS = (props: Props) => {
  const videoRef = useRef<HTMLDivElement>(null)
  const playerRef = useRef<videojs.Player | null>(null)
  const { options, onReady, title } = props
  const { isCastAvailable, castContext, isInitialized, isReady } = useChromecast()
  const [isLoading, setIsLoading] = useState(true)
  const [pluginsInitialized, setPluginsInitialized] = useState(false)

  // Initialize plugins first
  useEffect(() => {
    const initPlugins = async () => {
      const success = await initializePlugins()
      setPluginsInitialized(success)
    }

    initPlugins()
  }, [])

  // Initialize the player only after plugins are initialized
  useEffect(() => {
    if (!pluginsInitialized) return

    // Initialize the player regardless of Chromecast availability
    if (!playerRef.current && videoRef.current) {
      console.log('Initializing video player with following states:', {
        isCastAvailable,
        isInitialized,
        isReady,
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
        },
      })

      const videoElement = document.createElement('video-js')
      videoElement.classList.add('vjs-big-play-centered', 'vjs-fluid', 'rounded-xl', 'shadow-md')
      videoRef.current.appendChild(videoElement)

      // Force Chromecast button to be visible on mobile
      const playerOptions = {
        autoplay: false,
        controls: true,
        responsive: true,
        fluid: true,
        techOrder: ['html5', 'chromecast', 'airplay'],
        controlBar: {
          children: ['playToggle', 'volumePanel', 'currentTimeDisplay', 'timeDivider', 'durationDisplay', 'progressControl', 'chromecastButton', 'fullscreenToggle'],
        },
        ...options,
        plugins: {
          chromecast: {
            addButtonToControlBar: true, // Always try to add the button
            castContext: castContext,
            isCastApiAvailable: () => true, // Always show the button, it will be disabled if casting isn't available
          },
          airPlay: {
            addButtonToControlBar: true,
          },
          mux: {
            debug: true, // Enable debug for troubleshooting
            data: {
              env_key: process.env.REACT_APP_MUX_ENV_KEY,
              player_name: 'Web Video Player',
            },
          },
        },
      }

      // Always add Chromecast to techOrder
      playerOptions.techOrder.unshift('chromecast')

      try {
        const player = (playerRef.current = videojs(videoElement, playerOptions, () => {
          videojs.log('player is ready')
          console.log('Player initialized with options:', playerOptions)

          // Set up event listeners
          player.on('chromecastConnected', () => {
            console.log('Chromecast connected')
            console.log('Current source:', player.currentSource())
          })

          player.on('chromecastDisconnected', () => {
            console.log('Chromecast disconnected')
          })

          player.on('chromecastDevicesAvailable', () => {
            console.log('Chromecast devices available')
          })

          player.on('chromecastDevicesUnavailable', () => {
            console.log('Chromecast devices unavailable')
          })

          player.on('error', (error) => console.error('Player error:', error))

          // Log the control bar components
          console.log('Control bar components:', player.controlBar.children())

          // Set loading to false when player is ready
          setIsLoading(false)

          onReady && onReady(player)
        }))
      } catch (error) {
        console.error('Error initializing video player:', error)
        setIsLoading(false)
      }
    } else if (playerRef.current) {
      const player = playerRef.current
      player.autoplay(false)
      player.src(options.sources)
    }

    return () => {
      const player = playerRef.current
      if (player && !player.isDisposed()) {
        player.dispose()
        playerRef.current = null
      }
    }
  }, [options, onReady, title, castContext, isReady, isCastAvailable, isInitialized, pluginsInitialized])

  return (
    <div data-vjs-player className="w-full mx-auto relative">
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded-xl">
          <LoadingIndicator />
        </div>
      )}
      <div ref={videoRef} className="w-full" />
    </div>
  )
}

// Wrap the VideoJS component with the ChromecastProvider
export const VideoJSWithChromecast = (props: Props) => {
  return (
    <ChromecastProvider>
      <VideoJS {...props} />
    </ChromecastProvider>
  )
}

export default VideoJSWithChromecast
